import { waPhone } from "@/lib/phone";
import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JobWorkBadge, ProfitLine } from "@/components/SheetBilling";
import { Button } from "@/components/ui/button";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import {
  Eye, UploadSimple, CurrencyInr, Truck, CheckCircle, User,
  Phone, Package, WhatsappLogo, Sparkle, Pause, Play, Lightning, HandCoins, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * A clear, fresher-friendly order card with one big "next action" button.
 * The button changes based on the order's current status.
 */
export default function OrderCard({ order, onOpen, onQuickPayment, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "secondary_admin";
  const isDelivery = user?.role === "delivery";
  const fileRef = useRef(null);
  const cutFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCut, setUploadingCut] = useState(false);

  const uploadDesign = async (files, preOpenedWin = null) => {
    // Accept a single File (legacy) OR an array/FileList of Files (multi-upload).
    const list = files
      ? (files instanceof File ? [files] : Array.from(files))
      : [];
    if (list.length === 0) {
      if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
      return;
    }
    setUploading(true);
    try {
      let updated = null;
      // Upload each design sequentially so the customer approval flow only
      // triggers once and we can pipe the final response to WhatsApp.
      for (const f of list) {
        const fd = new FormData();
        fd.append("kind", "design_preview");
        fd.append("file", f);
        const { data } = await api.post(`/orders/${order.id}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updated = data;
      }
      toast.success(
        list.length > 1
          ? `${list.length} designs uploaded for #${order.order_number}. Sharing to ${updated.customer_name}…`
          : `Design uploaded for #${order.order_number}. Sharing preview to ${updated.customer_name}…`
      );
      const reviewUrl = `${window.location.origin}/design-review/${updated.order_number}`;
      const text =
        `*Shobhit Creation* — Order #${updated.order_number}\n\n` +
        `Namaste ${updated.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
        `${reviewUrl}`;
      const m = String(updated.mobile || "").replace(/\D/g, "");
      const phone = waPhone(m);
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      if (preOpenedWin && !preOpenedWin.closed) {
        try { preOpenedWin.location.href = waUrl; } catch (_e) {
          window.open(waUrl, "_blank");
        }
      } else {
        window.open(waUrl, "_blank");
      }
      onChanged?.();
    } catch (e) {
      if (preOpenedWin && !preOpenedWin.closed) {
        try { preOpenedWin.close(); } catch (_e) { /* ignore */ }
      }
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const shareDesignPreview = (o) => {
    const reviewUrl = `${window.location.origin}/design-review/${o.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${o.order_number}\n\n` +
      `Namaste ${o.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const m = String(o.mobile || "").replace(/\D/g, "");
    const phone = waPhone(m);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareDesignForApproval = () => {
    const reviewUrl = `${window.location.origin}/design-review/${order.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const m = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(m);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const bypassApproval = async () => {
    if (!window.confirm(
      `Bypass customer approval for order #${order.order_number}?\n\n` +
      `Ye tab karo jab customer verbally approve kar chuka ho (WhatsApp na use karta ho).\n` +
      `Order NEW ORDER → DESIGN PENDING move ho jayega.`
    )) return;
    try {
      await api.post(`/orders/${order.id}/approve-bypass`);
      toast.success(`Approval bypassed — order #${order.order_number} moved to Design Pending`);
      // Signal parent to refresh AND auto-switch to Design Pending tab so the
      // user immediately sees where their card went.
      onChanged?.({ goTo: "design_pending" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const uploadCuttingFile = async (file) => {
    if (!file) return;
    setUploadingCut(true);
    try {
      const fd = new FormData();
      fd.append("kind", "cutting_file");
      fd.append("file", file);
      const { data: updated } = await api.post(`/orders/${order.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (updated.status === "ready_to_cut" && order.status === "design_pending") {
        toast.success(`DXF uploaded — order moved to Ready to Cut 🎯`);
      } else {
        toast.success(`Cutting file uploaded for #${order.order_number}`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploadingCut(false); }
  };

  const shareWA = () => {
    const url = `${window.location.origin}/track/${order.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji\n` +
      `Amount: ₹${order.total_amount.toLocaleString("en-IN")}\n` +
      `${order.balance > 0 ? `Balance: ₹${order.balance.toLocaleString("en-IN")}` : "Fully Paid ✓"}\n\n` +
      `Track live: ${url}`;
    const m = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(m);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const advanceGateStage = async (target) => {
    try {
      await api.post(`/orders/${order.id}/status`, { status: target });
      toast.success(`Order #${order.order_number} moved to ${target.replace(/_/g, " ")}`);
      onChanged?.({ goTo: target });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const gateAction = async (action) => {
    const next = { start_framing: "framing_making", framing_done: "sheet_size_pending", cutting_done: "ready_for_installation", installation_done: "completed" }[action];
    if (!window.confirm(`#${order.order_number}: ${action.replace(/_/g, " ").toUpperCase()}?`)) return;
    try {
      await api.post(`/orders/${order.id}/gate/${action}`);
      toast.success(`Order #${order.order_number} → ${next.replace(/_/g, " ")}`);
      onChanged?.({ goTo: next });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const markDelivered = async () => {
    try {
      await api.post(`/orders/${order.id}/status`, { status: "delivered" });
      toast.success(`Order #${order.order_number} marked delivered`);
      onChanged?.({ goTo: "delivered" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const approveCredit = async () => {
    if (!window.confirm(
      `Order #${order.order_number} ko CREDIT SALE approve karna hai?\n\n` +
      `Balance ₹${Number(order.balance).toLocaleString("en-IN")} — customer ko baad me pay ` +
      `karna hoga. Approve karne ke baad delivery boy maal deliver kar sakega.`
    )) return;
    try {
      await api.patch(`/orders/${order.id}/approve-credit`, { approve: true });
      toast.success(`Credit approved for #${order.order_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const revokeCredit = async () => {
    if (!window.confirm(`Credit approval hataao?`)) return;
    try {
      await api.patch(`/orders/${order.id}/approve-credit`, { approve: false });
      toast.success("Credit approval revoked");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const action = getNextAction(order, {
    onOpen: () => onOpen(order.id),
    onUploadDesign: () => fileRef.current?.click(),
    onUploadCutting: () => cutFileRef.current?.click(),
    onQuickPayment: () => onQuickPayment?.(order.id),
    onAdvanceStage: advanceGateStage,
    onGateAction: gateAction,
    onMarkDelivered: markDelivered,
    uploading,
    userRole: user?.role,
  });

  const borderColor = getBorderColor(order.status);

  return (
    <div
      className={`surface p-5 rounded-sm border-l-4 ${borderColor} hover:bg-slate-800/40 transition-colors`}
      data-testid={`order-card-${order.order_number}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onOpen(order.id)}
              className="font-mono-num text-orange-400 text-xl font-bold hover:text-orange-300"
              data-testid={`open-order-${order.order_number}`}
            >
              #{order.order_number}
            </button>
            {order.project_type === "gate_railing" && (
              <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-cyan-500/15 border border-cyan-500/50 text-cyan-300">
                🚪 GATE
              </span>
            )}
            {order.work_type && (
              <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-violet-500/15 border border-violet-500/50 text-violet-300" data-testid={`work-type-chip-${order.order_number}`}>
                {WORK_TYPE_LABELS[order.work_type] || order.work_type}
              </span>
            )}
            {order.is_emergency && order.status !== "delivered" && (
              <span
                className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-red-500/15 border border-red-500 text-red-300 flex items-center gap-0.5 animate-pulse"
                data-testid={`emergency-badge-${order.order_number}`}
              >
                <Lightning size={10} weight="fill" /> EMERGENCY
              </span>
            )}
            {order.billing_mode === "job_work" && <JobWorkBadge small testid={`job-work-badge-${order.order_number}`} />}
            {/* Days-elapsed badge — only for Gate/Railing orders still in
                progress. Red blinking so it grabs attention on the shop
                floor and pushes owners to close long-pending projects. */}
            {order.project_type === "gate_railing" &&
              order.status !== "delivered" &&
              order.created_at && (
                <GateDaysBadge createdAt={order.created_at} />
              )}
            {order.project_type === "gate_railing" &&
              order.status !== "delivered" &&
              order.deadline_date && (
                <DeadlineBadge deadline={order.deadline_date} />
              )}
          </div>
          <div className="mt-1 text-slate-200 text-base truncate">{order.customer_name}</div>
          <a
            href={`tel:${order.mobile}`}
            className="text-cyan-300 font-mono-num text-xs flex items-center gap-1 mt-0.5 hover:text-cyan-200"
          >
            <Phone size={11} /> {order.mobile}
          </a>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge status={order.status} />
          <HoldPill order={order} onChanged={onChanged} />
        </div>
      </div>

      {/* Design approval status banners — only relevant while order is still
          in early stages. Once it's out for cutting/delivery/completed, the
          approval banner is stale noise. */}
      {order.design_approval_status === "pending" && order.design_preview &&
       (order.status === "new_order" || order.status === "design_pending" || order.status === "design_making") && (
        <div className="mb-3 rounded-sm border border-cyan-500/50 bg-cyan-500/10 px-2 py-1.5 text-[10px] font-mono-num tracking-widest text-cyan-300"
             data-testid={`approval-pending-${order.order_number}`}>
          ⏳ AWAITING CUSTOMER APPROVAL
        </div>
      )}
      {order.design_approval_status === "rejected" &&
       (order.status === "new_order" || order.status === "design_pending" || order.status === "design_making") && (
        <div className="mb-3 rounded-sm border-2 border-amber-500/70 bg-amber-500/15 px-2 py-1.5 text-[10px] font-mono-num tracking-widest text-amber-300"
             data-testid={`approval-rejected-${order.order_number}`}>
          ⚠ REUPLOAD THE DESIGN (CUSTOMER REJECTED)
        </div>
      )}

      {/* Emergency toggle — admin can flag ONLY ready-to-cut orders as
          urgent so they float to the top of the operator queue. */}
      {order.status === "ready_to_cut" && (
        <EmergencyToggle order={order} onUpdated={onChanged} />
      )}

      {/* Details row — hardware-only orders show HARDWARE/QTY, sheet orders show SHEETS/SQFT */}
      {(() => {
        const items = order.items || [];
        const allHardware = items.length > 0 && items.every(
          (i) => String(i.category || "").toLowerCase() === "hardware"
        );
        const totalQty = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
        return (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {order.plate_flow ? (
              <>
                {(order.cutting_plates || []).length ? (
                  <MiniStat label="Cutting plate" value={`${order.cutting_plates_total_sqft} sqft`} tone="cyan" />
                ) : ["framing_making", "structure_making"].includes(order.status) ? (
                  <button onClick={() => onOpen(order.id)} className="surface-2 px-2.5 py-2 rounded-sm text-left border border-red-500/70" data-testid={`cutting-plate-upload-${order.order_number}`}>
                    <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">CUTTING PLATE</div>
                    <div className="font-mono-num text-sm font-semibold text-red-400">Upload</div>
                  </button>
                ) : (
                  <MiniStat label="Cutting plate" value="—" />
                )}
                <MiniStat label="Gate sqft" value={order.total_sqft} />
              </>
            ) : allHardware ? (
              <>
                <MiniStat label="Hardware" value={items.length} />
                <MiniStat label="Qty" value={totalQty} tone="cyan" />
              </>
            ) : (
              <>
                <MiniStat label="Sheets" value={items.length} />
                <MiniStat label="SqFt" value={order.total_sqft} tone="cyan" />
              </>
            )}
            <MiniStat
              label={order.balance > 0 ? "Balance" : "Paid"}
              value={`₹${(order.balance > 0 ? order.balance : order.advance_paid).toLocaleString("en-IN")}`}
              tone={order.balance > 0 ? "orange" : "emerald"}
            />
          </div>
        );
      })()}

      {user?.role === "admin" && <div className="-mt-2 mb-3"><ProfitLine order={order} /></div>}
      {/* Amount bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] font-mono-num text-slate-500 tracking-widest mb-1">
          <span>PAID ₹{order.advance_paid.toLocaleString("en-IN")}</span>
          <span>TOTAL ₹{order.total_amount.toLocaleString("en-IN")}</span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded-sm overflow-hidden">
          <div
            className={"h-full transition-all " + (order.balance > 0 ? "bg-orange-500" : "bg-emerald-500")}
            style={{ width: `${Math.min(100, (order.advance_paid / Math.max(order.total_amount, 1)) * 100)}%` }}
          />
        </div>
      </div>

      {/* What to do next hint */}
      <div className="text-xs text-slate-400 mb-2 flex items-center gap-1.5">
        <Sparkle size={12} className="text-orange-400" weight="fill" />
        <span>{action.hint}</span>
      </div>

      {/* Primary action button */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={action.onClick}
          disabled={action.disabled || uploading || uploadingCut}
          className={"flex-1 rounded-sm h-11 font-heading tracking-widest " + action.className}
          data-testid={`action-${order.order_number}`}
        >
          {action.icon}
          {(uploading || uploadingCut) ? "UPLOADING…" : action.label}
        </Button>
        <Button
          onClick={() => onOpen(order.id)}
          variant="outline"
          className="rounded-sm h-11 border-slate-600 text-slate-300 hover:bg-slate-800 sm:w-11 sm:px-0"
          data-testid={`details-${order.order_number}`}
          title="Full order details"
        >
          <Eye size={16} />
          <span className="sm:hidden ml-1">Details</span>
        </Button>
        <Button
          onClick={shareWA}
          variant="outline"
          className="rounded-sm h-11 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 sm:w-11 sm:px-0"
          data-testid={`whatsapp-${order.order_number}`}
          title="Share on WhatsApp"
        >
          <WhatsappLogo size={16} weight="fill" />
          <span className="sm:hidden ml-1">WhatsApp</span>
        </Button>
      </div>

      {/* Ready-for-Delivery credit approval / delivery marker */}
      {order.status === "completed" && order.balance > 0 && !order.credit_approved && isAdmin && (
        <Button
          onClick={approveCredit}
          variant="outline"
          className="mt-2 w-full rounded-sm h-10 font-heading tracking-widest text-xs border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
          data-testid={`give-credit-${order.order_number}`}
          title="Approve credit sale — delivery can then be marked"
        >
          <HandCoins size={14} weight="fill" className="mr-1.5" />
          GIVE CREDIT (APPROVE)
        </Button>
      )}
      {order.status === "completed" && order.credit_approved && (
        <div
          className="mt-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 flex items-center gap-2 text-emerald-300 text-[11px] font-mono-num tracking-wide"
          data-testid={`credit-approved-chip-${order.order_number}`}
        >
          <ShieldCheck size={14} weight="fill" />
          <span className="flex-1 truncate">
            CREDIT APPROVED{order.credit_approved_by_name ? ` · ${order.credit_approved_by_name}` : ""}
          </span>
          {isAdmin && (
            <button
              type="button"
              onClick={revokeCredit}
              data-testid={`revoke-credit-${order.order_number}`}
              className="text-slate-400 hover:text-red-300 text-[10px]"
              title="Revoke credit approval"
            >REVOKE</button>
          )}
        </div>
      )}
      {order.status === "completed" && order.balance > 0 && order.credit_approved && (isDelivery || isAdmin) && (
        <Button
          onClick={async () => {
            if (!window.confirm(
              `Order #${order.order_number} deliver kar rahe ho?\n\n` +
              `Balance ₹${Number(order.balance).toLocaleString("en-IN")} baaki hai — ` +
              `"Collect Due" tab se baad me receive kar sakte hain.`
            )) return;
            await markDelivered();
          }}
          variant="outline"
          className="mt-2 w-full rounded-sm h-10 font-heading tracking-widest text-xs border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
          data-testid={`mark-delivered-credit-${order.order_number}`}
          title="Deliver on approved credit"
        >
          <Truck size={14} weight="fill" className="mr-1.5" />
          MARK DELIVERED (CREDIT — COLLECT LATER)
        </Button>
      )}

      {/* Show uploaded design/cutting file indicators */}
      {(order.design_preview || order.cutting_file) && (order.status === "new_order" || order.status === "design_pending" || order.status === "ready_to_cut") && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mono-num tracking-widest">
          {order.design_preview && (
            <span className="px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
              ✓ DESIGN
            </span>
          )}
          {order.cutting_file && (
            <span className="px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
              ✓ DXF
            </span>
          )}
        </div>
      )}

      {/* Inline approval actions — visible when design uploaded but customer approval still pending.
          For sheet cutting: while status is `new_order`.
          For gate & railing: while status is `design_making` (that stage's approval sub-flow). */}
      {order.design_preview && order.design_approval_status === "pending" &&
       (order.status === "new_order" || order.status === "design_making") && (
        <div className="mt-3 flex gap-2">
          <Button
            onClick={shareDesignForApproval}
            className="flex-1 min-w-0 rounded-sm h-11 px-2 font-heading tracking-wider text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid={`card-share-approval-${order.order_number}`}
          >
            <WhatsappLogo size={14} weight="fill" className="mr-1.5 shrink-0" />
            <span className="truncate">SHARE FOR APPROVAL</span>
          </Button>
          <Button
            onClick={bypassApproval}
            variant="outline"
            className="rounded-sm h-11 px-3 font-heading tracking-wider text-xs border-amber-500/60 text-amber-300 hover:bg-amber-500/10 shrink-0"
            data-testid={`card-bypass-approval-${order.order_number}`}
          >
            BYPASS
          </Button>
        </div>
      )}

      {/* Fabrication department attribution — only for gate/railing orders,
          shows which fabricator was assigned. */}
      {order.project_type === "gate_railing" && order.fabricator_name && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-sm bg-cyan-500/5 border border-cyan-500/30 px-2 py-1.5"
             data-testid={`order-fabricator-${order.order_number}`}>
          <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 flex items-center gap-1 min-w-0">
            <span className="text-slate-500">FABRICATION</span>
            <span className="truncate text-cyan-200 font-bold">{order.fabricator_name}</span>
            {order.fabricator_email && (
              <span className="text-slate-500 shrink-0 truncate">· {order.fabricator_email}</span>
            )}
          </div>
        </div>
      )}

      {/* Footer meta */}
      <div className="mt-3 flex items-center justify-between text-[10px] font-mono-num text-slate-500">
        <span>
          {new Date(order.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
        {order.created_by_name && (
          <span className="flex items-center gap-1">
            <User size={10} /> {order.created_by_name}
          </span>
        )}
      </div>

      {/* Hidden file input for inline design upload — supports multiple files */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.pdf"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) return;
          // Open blank window synchronously inside change handler (user gesture),
          // navigate it to WhatsApp URL once upload finishes.
          let waWindow = null;
          try {
            waWindow = window.open("about:blank", "_blank");
            if (waWindow) {
              try {
                waWindow.document.write(
                  '<!doctype html><title>Opening WhatsApp…</title>' +
                  '<style>body{background:#0f172a;color:#fff;font-family:system-ui,sans-serif;' +
                  'display:flex;align-items:center;justify-content:center;height:100vh;margin:0;text-align:center}</style>' +
                  '<div><div style="font-size:22px;font-weight:800;letter-spacing:.15em">SHOBHIT CREATION</div>' +
                  '<div style="margin-top:14px;color:#94a3b8">Uploading ' + files.length + ' design' + (files.length > 1 ? 's' : '') + ' & preparing WhatsApp…</div></div>'
                );
              } catch (_e) { /* ignore */ }
            }
          } catch (_e) { waWindow = null; }
          uploadDesign(files, waWindow);
          e.target.value = "";
        }}
        data-testid={`inline-design-upload-${order.order_number}`}
      />
      {/* Hidden file input for inline cutting-file (DXF/DWG) upload */}
      <input
        ref={cutFileRef}
        type="file"
        accept=".dxf,.dwg,.DXF,.DWG"
        hidden
        onChange={(e) => uploadCuttingFile(e.target.files?.[0])}
        data-testid={`inline-cutting-upload-${order.order_number}`}
      />
    </div>
  );
}

function MiniStat({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-200",
    cyan: "text-cyan-300",
    orange: "text-orange-400",
    emerald: "text-emerald-400",
  };
  return (
    <div className="surface-2 px-2.5 py-2 rounded-sm">
      <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">{label.toUpperCase()}</div>
      <div className={`font-mono-num text-sm font-semibold ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

function getBorderColor(status) {
  return ({
    draft: "border-slate-600",
    new_order: "border-amber-500",
    design_pending: "border-cyan-500",
    ready_to_cut: "border-orange-500",
    in_cutting: "border-orange-400",
    framing_making: "border-violet-500",
    sheet_size_pending: "border-cyan-400",
    ready_for_installation: "border-violet-400",
    design_making: "border-amber-500",
    design_cutting: "border-orange-400",
    completed: "border-emerald-500",
    delivered: "border-emerald-400",
  })[status] || "border-slate-600";
}

const WORK_TYPE_LABELS = { gate: "GATE", railing: "RAILING", window: "WINDOW", front_elevation: "FRONT ELEVATION" };

function getNextAction(order, handlers) {
  const isGate = order.project_type === "gate_railing";

  if (isGate && order.gate_v2) {
    const gv = (hint, label, onClick, cls = "bg-violet-500 hover:bg-violet-400 text-white") => ({
      hint, label, onClick, className: cls, icon: <Package size={16} className="mr-1" weight="bold" />,
    });
    switch (order.status) {
      case "new_order": return gv("New gate/railing order. Framing shuru kariye.", "START FRAMING", () => handlers.onGateAction("start_framing"));
      case "framing_making": return gv("Framing chal rahi hai. Frame ready hone pe FRAMING DONE.", "FRAMING DONE", () => handlers.onGateAction("framing_done"), "bg-amber-500 hover:bg-amber-400 text-slate-900");
      case "sheet_size_pending": return gv("Framing done. Har work item ki plate sizes bhariye — stock tabhi katega.", "UPLOAD SHEET SIZE", handlers.onOpen, "bg-cyan-500 hover:bg-cyan-400 text-slate-900");
      case "design_making":
        if (!order.design_preview) return gv("Sheet sizes saved. Design upload karke customer ko bhejiye.", "UPLOAD DESIGN", handlers.onUploadDesign, "bg-amber-500 hover:bg-amber-400 text-slate-900");
        if (order.design_approval_status !== "confirmed") return gv("Customer approval ka wait — share / bypass row use kariye.", "VIEW APPROVAL", handlers.onOpen, "btn-industrial");
        return gv("Design approved. DXF upload karte hi Ready for Cutting.", "UPLOAD DXF FILE", handlers.onUploadCutting, "bg-cyan-500 hover:bg-cyan-400 text-slate-900");
      case "ready_to_cut": return gv("Operator ke queue me. Machine pe cutting shuru hogi.", "VIEW CUTTING JOB", handlers.onOpen, "btn-industrial");
      case "in_cutting":
      case "design_cutting": return gv("Cutting chal rahi hai.", "CUTTING DONE", () => handlers.onGateAction("cutting_done"), "bg-orange-500 hover:bg-orange-400 text-white");
      case "ready_for_installation": return gv("Fabricator panels install kar raha hai.", "INSTALLATION DONE", () => handlers.onGateAction("installation_done"));
      default: break;
    }
  }
  // Gate & Railing has its own workflow — override on top of the sheet defaults
  if (isGate) {
    switch (order.status) {
      case "new_order":
        return {
          hint: "New gate/railing order. Start with the metal frame — welding & fitting.",
          label: "START FRAMING",
          icon: <Package size={16} className="mr-1" weight="bold" />,
          className: "bg-violet-500 hover:bg-violet-400 text-white",
          onClick: () => handlers.onAdvanceStage("framing_making"),
        };
      case "framing_making":
        if (order.plate_flow) {
          const has = (order.cutting_plates || []).length > 0;
          return {
            hint: has ? "Cutting sizes bhar diye. Check karke FRAMING DONE · SEND TO DESIGN dabaiye." : "Framing chal rahi hai. Frame ke baad cutting sheet sizes upload kariye.",
            label: has ? "REVIEW PLATE SIZE" : "UPLOAD PLATE SIZE",
            icon: <UploadSimple size={16} className="mr-1" weight="bold" />,
            className: "bg-amber-500 hover:bg-amber-400 text-slate-900",
            onClick: handlers.onOpen,
          };
        }
        return {
          hint: "Frame in progress. Once metal frame is welded, move to Design Making to prepare decorative panels.",
          label: "FRAMING DONE — START DESIGN",
          icon: <Package size={16} className="mr-1" weight="bold" />,
          className: "bg-amber-500 hover:bg-amber-400 text-slate-900",
          onClick: () => handlers.onAdvanceStage("design_making"),
        };
      case "design_making":
        // Design Making stage runs the SAME sub-flow as sheet cutting:
        // upload design → get customer approval → upload DXF → auto-advance to design_cutting
        if (!order.design_preview) {
          return {
            hint: "Upload the decorative panel design (image / PDF) to send to customer.",
            label: "UPLOAD DESIGN",
            icon: <UploadSimple size={16} className="mr-1" weight="bold" />,
            className: "bg-amber-500 hover:bg-amber-400 text-slate-900",
            onClick: handlers.onUploadDesign,
          };
        }
        // Design uploaded but approval still pending? UI shows the SHARE/BYPASS row separately.
        // Once approval is confirmed, next step is DXF upload.
        return {
          hint: "Design approved. Upload the DXF/DWG cutting file to send it to the machine.",
          label: "UPLOAD DXF FILE",
          icon: <UploadSimple size={16} className="mr-1" weight="bold" />,
          className: "bg-cyan-500 hover:bg-cyan-400 text-slate-900",
          onClick: handlers.onUploadCutting,
        };
      case "design_cutting":
        return {
          hint: "Decorative panels cutting on the CNC machine. Operator will mark done.",
          label: "TRACK CUTTING",
          icon: <Eye size={16} className="mr-1" weight="bold" />,
          className: "bg-orange-500/20 border border-orange-500 text-orange-300 hover:bg-orange-500/30",
          onClick: handlers.onOpen,
        };
      // completed / delivered fall through to the shared block below.
      default:
        break;
    }
  }

  switch (order.status) {
    case "new_order":
      return {
        hint: "Fresh order received. Upload the design(s) — you can select multiple images/PDFs at once.",
        label: "UPLOAD DESIGN",
        icon: <UploadSimple size={16} className="mr-1" weight="bold" />,
        className: "bg-amber-500 hover:bg-amber-400 text-slate-900",
        onClick: handlers.onUploadDesign,
      };
    case "design_pending":
      return {
        hint: "Design shared. Upload the DXF/DWG cutting file to send it to the machine.",
        label: "UPLOAD DXF FILE",
        icon: <UploadSimple size={16} className="mr-1" weight="bold" />,
        className: "bg-cyan-500 hover:bg-cyan-400 text-slate-900",
        onClick: handlers.onUploadCutting,
      };
    case "ready_to_cut":
      return {
        hint: "Ready for the operator. Cutting file waiting on machine.",
        label: "VIEW CUTTING JOB",
        icon: <Eye size={16} className="mr-1" weight="bold" />,
        className: "btn-industrial",
        onClick: handlers.onOpen,
      };
    case "in_cutting":
      return {
        hint: "On the machine now — operator is cutting.",
        label: "TRACK PROGRESS",
        icon: <Eye size={16} className="mr-1" weight="bold" />,
        className: "bg-orange-500/20 border border-orange-500 text-orange-300 hover:bg-orange-500/30",
        onClick: handlers.onOpen,
      };
    case "completed":
      return {
        hint: order.balance > 0
          ? "Cutting done. Collect final payment on delivery."
          : "Fully paid. Ready to deliver!",
        label: order.balance > 0 ? "TAKE FINAL PAYMENT" : "MARK DELIVERED",
        icon: order.balance > 0
          ? <CurrencyInr size={16} className="mr-1" weight="bold" />
          : <Truck size={16} className="mr-1" weight="fill" />,
        className: "bg-emerald-600 hover:bg-emerald-500 text-white",
        onClick: order.balance > 0 ? handlers.onQuickPayment : handlers.onMarkDelivered,
      };
    case "delivered":
      return {
        hint: order.balance > 0
          ? `Delivered but ₹${Number(order.balance).toLocaleString("en-IN")} due — collect payment.`
          : "Order closed. All payments received.",
        label: order.balance > 0 ? "COLLECT DUE" : "VIEW RECEIPT",
        icon: order.balance > 0
          ? <CurrencyInr size={16} className="mr-1" weight="bold" />
          : <CheckCircle size={16} className="mr-1" weight="fill" />,
        className: order.balance > 0
          ? "bg-orange-500 hover:bg-orange-400 text-white"
          : "bg-slate-700 hover:bg-slate-600 text-slate-200",
        onClick: order.balance > 0 ? handlers.onQuickPayment : handlers.onOpen,
      };
    default:
      return {
        hint: "Open order to see details.",
        label: "VIEW ORDER",
        icon: <Package size={16} className="mr-1" />,
        className: "bg-slate-700 hover:bg-slate-600 text-white",
        onClick: handlers.onOpen,
      };
  }
}


/* --------------------------------------------------------------------
   EmergencyToggle — admin-only checkbox that flags an active order as
   urgent. The order jumps to the top of the operator queue and shows a
   red "🔥 EMERGENCY" pill wherever it appears.
-------------------------------------------------------------------- */
function EmergencyToggle({ order, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const on = !!order.is_emergency;

  const toggle = async (next) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/emergency`, {
        is_emergency: next,
      });
      onUpdated && onUpdated(data);
      toast.success(next
        ? "🔥 Emergency ON — operator queue mein top pe aa gaya"
        : "Emergency OFF — normal order priority");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <label
      className={
        "mb-3 flex items-center justify-between gap-3 px-3 py-2 rounded-sm cursor-pointer transition-colors border-2 " +
        (on
          ? "bg-red-500/15 border-red-500/60 hover:bg-red-500/20"
          : "bg-slate-800/40 border-slate-700 hover:border-red-500/40")
      }
      data-testid={`emergency-toggle-${order.order_number}`}
    >
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={on}
          disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          className="w-4 h-4 accent-red-500 cursor-pointer"
          data-testid={`emergency-checkbox-${order.order_number}`}
        />
        <span className={
          "font-mono-num text-[11px] tracking-widest font-bold " +
          (on ? "text-red-300" : "text-slate-400")
        }>
          {on ? "🔥 EMERGENCY — TOP PRIORITY" : "MARK AS EMERGENCY"}
        </span>
      </div>
      {on && (
        <span className="text-[9px] font-mono-num tracking-widest text-red-400 bg-red-500/20 border border-red-500/40 px-1.5 py-0.5 rounded-sm animate-pulse">
          URGENT
        </span>
      )}
    </label>
  );
}

/* -----------------------------------------------------------
   GateDaysBadge — red blinking "N DAYS" pill for long-running
   gate/railing orders. Reads created_at, computes calendar
   days elapsed. Tone escalates with age:
     • 0-2 days: amber (fresh)
     • 3+ days: red + pulsing (attention)
------------------------------------------------------------ */

/**
 * HoldPill — compact icon button next to the StatusBadge for quick hold/resume
 * without opening the full order dialog.
 */
function HoldPill({ order, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const held = order.status === "on_hold";
  const closed = order.status === "delivered" || order.status === "draft";
  if (closed) return null;

  const putOnHold = async (e) => {
    e.stopPropagation();
    const reason = window.prompt("Customer ne kya bola? (Reason for hold)", "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/hold`, { on_hold: true, reason });
      toast.success(`#${order.order_number} put on hold`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Hold failed");
    } finally { setBusy(false); }
  };
  const resume = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/hold`, { on_hold: false });
      toast.success(`#${order.order_number} resumed`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Resume failed");
    } finally { setBusy(false); }
  };

  if (held) {
    return (
      <button
        onClick={resume}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/70 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-[10px] font-mono-num font-bold tracking-widest px-2 h-6 transition-colors disabled:opacity-50"
        title={order.hold_reason ? `Reason: ${order.hold_reason}` : "Resume this order"}
        data-testid={`resume-btn-${order.order_number}`}
      >
        <Play size={11} weight="fill" /> RESUME
      </button>
    );
  }
  return (
    <button
      onClick={putOnHold}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded-sm border border-slate-300 hover:border-orange-500 hover:bg-orange-500/10 text-slate-500 hover:text-orange-600 text-[10px] font-mono-num font-bold tracking-widest px-2 h-6 transition-colors disabled:opacity-50"
      title="Put this order on hold (customer paused)"
      data-testid={`hold-btn-${order.order_number}`}
    >
      <Pause size={11} weight="fill" /> HOLD
    </button>
  );
}

function GateDaysBadge({ createdAt }) {
  const days = React.useMemo(() => {
    if (!createdAt) return 0;
    const then = new Date(createdAt);
    if (Number.isNaN(then.getTime())) return 0;
    const now = new Date();
    // Compare calendar days (midnight-to-midnight) so a 3pm order created
    // yesterday shows "1 din" even at 9am next morning, not "0 din".
    const t1 = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
    const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const d = Math.max(0, Math.round((t2 - t1) / 86400000));
    return d;
  }, [createdAt]);

  if (days <= 0) return null;
  const isOld = days >= 3;
  const cls = isOld
    ? "bg-red-500/25 border border-red-500/70 text-red-200 animate-pulse"
    : "bg-amber-500/15 border border-amber-500/50 text-amber-200";
  return (
    <span
      className={"text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm font-bold " + cls}
      data-testid="gate-days-badge"
      title={`Order ko ${days} din ho gaye hain — pending fabrication`}
    >
      {days} DIN
    </span>
  );
}

/* -----------------------------------------------------------
   DeadlineBadge — internal deadline (never shown to customer).
   Renders "DUE 12 Jul" or "OVERDUE 3d" / "DUE TODAY" depending
   on how many days away it is. Colour escalates as we get close.
------------------------------------------------------------ */
function DeadlineBadge({ deadline }) {
  const info = React.useMemo(() => {
    if (!deadline) return null;
    const d = new Date(deadline);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const t1 = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const t2 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const diff = Math.round((t1 - t2) / 86400000);
    const dateLabel = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    return { diff, dateLabel };
  }, [deadline]);
  if (!info) return null;
  const { diff, dateLabel } = info;
  let label, cls;
  if (diff < 0) {
    label = `OVERDUE ${Math.abs(diff)}D`;
    cls = "bg-red-500/30 border border-red-500/70 text-red-100 animate-pulse";
  } else if (diff === 0) {
    label = "DUE TODAY";
    cls = "bg-red-500/25 border border-red-500/70 text-red-100 animate-pulse";
  } else if (diff <= 2) {
    label = `DUE ${diff}D`;
    cls = "bg-amber-500/25 border border-amber-500/70 text-amber-100";
  } else {
    label = `DUE ${dateLabel}`;
    cls = "bg-cyan-500/15 border border-cyan-500/50 text-cyan-200";
  }
  return (
    <span
      className={"text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm font-bold " + cls}
      data-testid="order-deadline-badge"
      title={`Internal deadline: ${dateLabel} (${diff >= 0 ? diff + " din baaki" : Math.abs(diff) + " din overdue"})`}
    >
      🔒 {label}
    </span>
  );
}
